import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { MiaV3Service } from '../services/mia-v3.service';
import { custoEmReaisFormatado } from './custo-ia';

/**
 * Tela de QA da Mia (só admin do MeuCardápio), em três abas:
 * — Visão geral: KPIs, distribuição de vereditos, padrões de falha e varredura;
 * — Conversas: master-detail com a conversa e o laudo do juiz por cima;
 * — Relatórios: leitura dos relatórios do período (montados das avaliações, sem IA).
 * Cliques na visão geral e refs "#id" dos relatórios caem na aba Conversas.
 */

type Aba = 'visao' | 'conversas' | 'relatorios';

const VEREDITOS: { [chave: string]: { rotulo: string, classe: string } } = {
  POSITIVA: { rotulo: 'Positiva', classe: 'positiva' },
  POSITIVA_COM_RESSALVAS: { rotulo: 'Com ressalvas', classe: 'ressalva' },
  NEGATIVA: { rotulo: 'Negativa', classe: 'negativa' },
  NAO_AVALIAVEL: { rotulo: 'Não avaliável', classe: 'neutra' },
};

const CATEGORIAS: { [chave: string]: string } = {
  INFORMACAO_ERRADA: 'Informação errada',
  NAO_ENTENDEU_PEDIDO: 'Não entendeu o pedido',
  IGNOROU_CLIENTE: 'Ignorou o cliente',
  LOOP: 'Repetiu-se em loop',
  NAO_ESCALOU: 'Não escalou para humano',
  PROMESSA_SEM_ACAO: 'Prometeu sem agir',
  FLUXO_TRAVADO: 'Fluxo travou',
  ACAO_INDEVIDA: 'Ação indevida',
  FORA_DE_ESCOPO: 'Saiu do papel (fora de escopo)',
  IDIOMA_ERRADO: 'Respondeu em outro idioma',
  OUTRO: 'Outro',
};

interface BarraCategoria {
  categoria: string;
  rotulo: string;
  total: number;
  percentual: number;
}

/** Fatia da barra de distribuição de vereditos (fila = ainda sem veredito). */
interface FatiaDaDistribuicao {
  chave: string;
  rotulo: string;
  total: number;
  veredito: string | null;
}

/** Pedaço do texto de um relatório: texto corrido, "#id" clicável ou citação. */
interface ParteDoRelatorio {
  tipo: 'texto' | 'ref' | 'citacao';
  texto?: string;
  atendimentoId?: number;
  citacao?: string;
}

/** Item do relatório recém-separado do texto, antes de virar partes. */
interface ItemCruDoRelatorio {
  numero: string;
  marcador: string | null;
  titulo: string;
  linhas: string[];
}

const PRIORIDADES: { [chave: string]: string } = {
  ALTA: 'Alta',
  MEDIA: 'Média',
  BAIXA: 'Baixa',
};

/** Linha unificada da lista: avaliado ou não, aberto ou não. */
interface ItemDaLista {
  atendimentoId: number;
  telefone: string;
  abertoEm: string;
  aberto: boolean;
  veredito: string | null;
  titulo: string | null;
  resumo: string | null;
  desfecho: string | null;
  motivo: string | null;
  disparo: string | null;
  categorias: string[];
}

@Component({
  selector: 'app-qualidade-mia-v3',
  templateUrl: './qualidade-mia-v3.component.html',
  styleUrls: ['./qualidade-mia-v3.component.scss']
})
export class QualidadeMiaV3Component implements OnInit {
  aba: Aba = 'visao';

  periodos = [
    { dias: 1, rotulo: 'Hoje' },
    { dias: 7, rotulo: '7 dias' },
    { dias: 30, rotulo: '30 dias' }
  ];
  diasSelecionados = 7;
  filtroVeredito = '';
  filtroCategoria = '';
  filtroFila = false;

  carregando = true;
  erro = '';

  resumo: any = null;
  categorias: BarraCategoria[] = [];
  itens: ItemDaLista[] = [];
  totalNaFila = 0;
  distribuicao: FatiaDaDistribuicao[] = [];

  // Config da varredura automática
  salvandoConfig = false;
  configSalva = false;
  config = { ativa: false, desfechos: [] as string[], amostragemPedidoGerado: 0 };
  desfechosDisponiveis = [
    { chave: 'SEM_PEDIDO', rotulo: 'Não converteu (SEM_PEDIDO)' },
    { chave: 'INCONCLUSIVO', rotulo: 'Conversa morreu (INCONCLUSIVO)' },
    { chave: 'NAO_APLICAVEL', rotulo: 'Pós-venda / takeover (NAO_APLICAVEL)' },
  ];

  // Relatórios narrativos
  relatorios: any[] = [];
  relatorioAberto: any = null;
  gerandoRelatorio = false;

  // Conversa selecionada (painel direito)
  selecionadoId: number | null = null;
  detalhe: any = null;            // { avaliacao|null, falhas, atendimento, turnos }
  falhasPorTurno: { [turnoId: number]: any[] } = {};
  carregandoDetalhe = false;
  avaliando = false;

  constructor(private miaV3Service: MiaV3Service, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.carregue(true);
    this.carregueConfig();
    this.carregueRelatorios();
  }

  selecioneAba(aba: Aba): void {
    this.aba = aba;
  }

  // ---------- lista ----------

  carregue(selecionarPrimeiro = false): void {
    this.carregando = true;
    this.erro = '';

    const filtro = {
      dias: this.diasSelecionados,
      veredito: this.filtroVeredito || undefined,
      categoria: this.filtroCategoria || undefined,
    };

    Promise.all([
      this.miaV3Service.obtenhaAvaliacoes(filtro),
      this.miaV3Service.obtenhaNaoAvaliados({ dias: this.diasSelecionados }),
    ]).then(([avaliacoes, naoAvaliados]: any[]) => {
      this.resumo = avaliacoes?.resumo || null;
      this.totalNaFila = (naoAvaliados?.atendimentos || []).length;
      this.itens = this.monteLista(avaliacoes?.avaliacoes || [], naoAvaliados?.atendimentos || []);
      this.distribuicao = this.monteDistribuicao();

      const linhas: any[] = avaliacoes?.categorias || [];
      const maior = Math.max(1, ...linhas.map(linha => Number(linha.total) || 0));
      this.categorias = linhas.map(linha => ({
        categoria: linha.categoria,
        rotulo: CATEGORIAS[linha.categoria] || linha.categoria,
        total: Number(linha.total) || 0,
        percentual: Math.round((Number(linha.total) || 0) * 100 / maior),
      }));

      // No mobile a seleção abriria a conversa por cima da lista logo no load.
      if (selecionarPrimeiro && !this.selecionadoId && this.itens.length && window.innerWidth > 820) {
        this.selecione(this.itens[0]);
      }
    }).catch(() => {
      this.erro = 'Não consegui carregar as avaliações. A Mia V3 está de pé?';
    }).then(() => this.carregando = false);
  }

  /**
   * Uma lista só: em andamento primeiro, depois por início desc. Com filtro de
   * veredito/categoria ativo os não-avaliados saem — não têm o que casar.
   */
  private monteLista(avaliadas: any[], naoAvaliados: any[]): ItemDaLista[] {
    const porInicio = (a: ItemDaLista, b: ItemDaLista) => {
      if (a.aberto !== b.aberto) return a.aberto ? -1 : 1;
      return String(b.abertoEm).localeCompare(String(a.abertoEm));
    };

    const pendentesDaFila: ItemDaLista[] = (naoAvaliados || []).map(atendimento => ({
      atendimentoId: atendimento.id,
      telefone: atendimento.telefone,
      abertoEm: atendimento.abertoEm,
      aberto: !!atendimento.aberto,
      veredito: null,
      titulo: null,
      resumo: null,
      desfecho: atendimento.aberto ? null : atendimento.desfecho,
      motivo: atendimento.aberto ? null : atendimento.motivo,
      disparo: null,
      categorias: [],
    }));

    // "Na fila": só o que ainda não passou pelo juiz.
    if (this.filtroFila) return pendentesDaFila.sort(porInicio);

    const doJuiz: ItemDaLista[] = (avaliadas || []).map(avaliacao => ({
      atendimentoId: avaliacao.atendimentoId,
      telefone: avaliacao.telefone,
      abertoEm: avaliacao.abertoEm,
      aberto: false,
      veredito: avaliacao.veredito,
      titulo: avaliacao.titulo || null,
      resumo: avaliacao.resumo,
      desfecho: avaliacao.desfecho,
      motivo: avaliacao.motivo,
      disparo: avaliacao.disparo,
      categorias: String(avaliacao.categorias || '').split(',').filter((categoria: string) => categoria),
    }));

    const filtrando = !!(this.filtroVeredito || this.filtroCategoria);
    const pendentes: ItemDaLista[] = filtrando ? [] : pendentesDaFila;

    // Avaliado de atendimento aberto não existe na lista do juiz com flag — o
    // pendente já traz. União simples, ordenada: em andamento no topo.
    return [...doJuiz, ...pendentes].sort(porInicio);
  }

  /** Barra de distribuição: os vereditos do período + o que ainda está na fila. */
  private monteDistribuicao(): FatiaDaDistribuicao[] {
    const resumo = this.resumo || {};
    const fatias: FatiaDaDistribuicao[] = [
      { chave: 'positiva', rotulo: 'Positiva', total: Number(resumo.positivas) || 0, veredito: 'POSITIVA' },
      { chave: 'ressalva', rotulo: 'Com ressalvas', total: Number(resumo.comRessalvas) || 0, veredito: 'POSITIVA_COM_RESSALVAS' },
      { chave: 'negativa', rotulo: 'Negativa', total: Number(resumo.negativas) || 0, veredito: 'NEGATIVA' },
      { chave: 'neutra', rotulo: 'Não avaliável', total: Number(resumo.naoAvaliaveis) || 0, veredito: 'NAO_AVALIAVEL' },
      { chave: 'fila', rotulo: 'Na fila', total: this.totalNaFila, veredito: null },
    ];
    return fatias.filter(fatia => fatia.total > 0);
  }

  selecionePeriodo(dias: number): void {
    if (dias === this.diasSelecionados) return;
    this.diasSelecionados = dias;
    this.carregue();
  }

  /** Clique numa causa filtra a lista e leva para a aba Conversas (de novo limpa). */
  filtrePorCategoria(categoria: string): void {
    this.filtroFila = false;
    this.filtroCategoria = this.filtroCategoria === categoria ? '' : categoria;
    this.aba = 'conversas';
    this.carregue();
  }

  /** Clique numa fatia/legenda da distribuição: filtra e leva para Conversas. */
  cliqueFatia(fatia: FatiaDaDistribuicao): void {
    if (fatia.veredito) {
      this.filtroFila = false;
      this.filtroVeredito = this.filtroVeredito === fatia.veredito ? '' : fatia.veredito;
    } else {
      this.filtroFila = !this.filtroFila;
      if (this.filtroFila) {
        this.filtroVeredito = '';
        this.filtroCategoria = '';
      }
    }
    this.aba = 'conversas';
    this.carregue();
  }

  fatiaAtiva(fatia: FatiaDaDistribuicao): boolean {
    return fatia.veredito ? this.filtroVeredito === fatia.veredito : this.filtroFila;
  }

  get temFiltroDeFatia(): boolean {
    return !!(this.filtroVeredito || this.filtroFila);
  }

  get temFiltro(): boolean {
    return !!(this.filtroVeredito || this.filtroCategoria || this.filtroFila);
  }

  limpeFiltros(): void {
    this.filtroVeredito = '';
    this.filtroCategoria = '';
    this.filtroFila = false;
    this.carregue();
  }

  // ---------- números do resumo ----------

  /** Avaliáveis = avaliados menos os "não avaliável". */
  get avaliaveis(): number {
    const resumo = this.resumo || {};
    return (Number(resumo.positivas) || 0) + (Number(resumo.comRessalvas) || 0) + (Number(resumo.negativas) || 0);
  }

  get aprovacao(): number {
    const avaliaveis = this.avaliaveis;
    return avaliaveis ? Math.round((Number(this.resumo?.positivas) || 0) * 100 / avaliaveis) : 0;
  }

  get custoPorAvaliacao(): string {
    const avaliados = Number(this.resumo?.avaliados) || 0;
    if (!avaliados || !this.resumo?.custoUsd) return '';
    return custoEmReaisFormatado(Number(this.resumo.custoUsd) / avaliados);
  }

  get totalFalhas(): number {
    return this.categorias.reduce((soma, categoria) => soma + categoria.total, 0);
  }

  rotuloVeredito(veredito: string | null): string {
    if (!veredito) return 'Não avaliado';
    return (VEREDITOS[veredito] || { rotulo: veredito }).rotulo;
  }

  classeVeredito(veredito: string | null): string {
    if (!veredito) return 'pendente';
    return (VEREDITOS[veredito] || { classe: 'neutra' }).classe;
  }

  rotuloCategoria(categoria: string): string {
    return CATEGORIAS[categoria] || categoria;
  }

  custoFormatado(custoUsd: number): string {
    return custoEmReaisFormatado(Number(custoUsd) || 0);
  }

  rastreieItem(_indice: number, item: ItemDaLista): number {
    return item.atendimentoId;
  }

  // ---------- config ----------

  carregueConfig(): void {
    this.miaV3Service.obtenhaConfigAvaliacao().then((resposta: any) => {
      const config = resposta?.config;
      if (!config) return;
      this.config = {
        ativa: !!config.ativa,
        desfechos: String(config.desfechos || '').split(',').filter((desfecho: string) => desfecho),
        amostragemPedidoGerado: Number(config.amostragemPedidoGerado) || 0,
      };
    }).catch(() => {});
  }

  desfechoLigado(chave: string): boolean {
    return this.config.desfechos.includes(chave);
  }

  alterneDesfecho(chave: string): void {
    this.config.desfechos = this.desfechoLigado(chave)
      ? this.config.desfechos.filter(desfecho => desfecho !== chave)
      : [...this.config.desfechos, chave];
  }

  salveConfig(): void {
    this.salvandoConfig = true;
    this.configSalva = false;
    this.miaV3Service.salveConfigAvaliacao({
      ativa: this.config.ativa,
      desfechos: this.config.desfechos.join(','),
      amostragemPedidoGerado: Number(this.config.amostragemPedidoGerado) || 0,
    }).then(() => {
      this.configSalva = true;
      setTimeout(() => this.configSalva = false, 3000);
    }).catch(() => {
      this.erro = 'Não consegui salvar a configuração.';
    }).then(() => this.salvandoConfig = false);
  }

  // ---------- relatórios ----------

  carregueRelatorios(): void {
    this.miaV3Service.obtenhaRelatoriosAvaliacao().then((resposta: any) => {
      this.relatorios = (resposta?.relatorios || []).map((relatorio: any) => this.estruture(relatorio));
      if (!this.relatorioAberto && this.relatorios.length) this.relatorioAberto = this.relatorios[0];
    }).catch(() => {});
  }

  /**
   * Estrutura o texto do relatório para a tela. Relatório novo é Markdown com
   * um heading por item ("## [PRIORIDADE] Título — #id"); os anteriores usam
   * linha-título numerada ou parágrafo numerado. O título termina no "#id"
   * clicável — é o link que leva do padrão para a cena — e o corpo vira HTML
   * renderizado do md, com "#id" e citações destacados. Pré-computado — nada
   * de método no template devolvendo valor novo a cada change detection.
   */
  private estruture(relatorio: any): any {
    const texto = String(relatorio?.texto || '');
    const itens = this.separeItens(texto);

    relatorio.totalItens = itens.length;
    relatorio.altas = itens.filter(item => item.marcador === 'ALTA').length;

    relatorio.itens = itens.length ? itens.map(item => {
      const corpo = item.linhas.join('\n').trim();
      const geral = item.marcador === 'GERAL';
      return {
        numero: item.numero,
        // o balanço fala do período inteiro: só ganha link se o próprio título trouxer um "#id"
        tituloPartes: this.partaTitulo(item.titulo, geral ? '' : corpo),
        prioridade: geral ? null : item.marcador,
        geral,
        html: this.rendaMarkdown(corpo),
      };
    }) : null;
    relatorio.html = this.rendaMarkdown(texto);
    return relatorio;
  }

  /**
   * Quebra o relatório em itens: primeiro pelo heading Markdown
   * ("## [ALTA] Título"); depois pela linha-título com selo ("N. [ALTA] Título");
   * por fim — relatório antigo — pelo parágrafo numerado ("N. texto…"),
   * que vira item sem selo nem título.
   */
  private separeItens(texto: string): ItemCruDoRelatorio[] {
    const mdHeading = /^#{2,3}\s*\[(ALTA|M[ÉE]DIA|BAIXA|GERAL)\]\s*(.+)$/i;
    const comSelo = /^\s*(\d+)\.\s*\[(ALTA|M[ÉE]DIA|BAIXA|GERAL)\]\s*(.+)$/i;
    const numerado = /^\s*(\d+)[.)]\s+(.*)$/;

    type Cabecalho = (combinou: RegExpMatchArray, indice: number) => ItemCruDoRelatorio;
    const doMd: Cabecalho = (combinou, indice) => ({
      numero: String(indice + 1),
      marcador: combinou[1].toUpperCase().replace('É', 'E'),
      titulo: combinou[2].trim(),
      linhas: [],
    });
    const doSelo: Cabecalho = combinou => ({
      numero: combinou[1],
      marcador: combinou[2].toUpperCase().replace('É', 'E'),
      titulo: combinou[3].trim(),
      linhas: [],
    });
    const doNumerado: Cabecalho = combinou => ({
      numero: combinou[1], marcador: null, titulo: '', linhas: [combinou[2]],
    });

    const colha = (padrao: RegExp, monte: Cabecalho): ItemCruDoRelatorio[] => {
      const itens: ItemCruDoRelatorio[] = [];
      let atual: ItemCruDoRelatorio | null = null;
      for (const linha of texto.split('\n')) {
        const combinou = linha.match(padrao);
        if (combinou) {
          atual = monte(combinou, itens.length);
          itens.push(atual);
        } else if (atual) {
          atual.linhas.push(linha);
        }
      }
      return itens;
    };

    const emMarkdown = colha(mdHeading, doMd);
    if (emMarkdown.length) return emMarkdown;
    const comTitulo = colha(comSelo, doSelo);
    return comTitulo.length ? comTitulo : colha(numerado, doNumerado);
  }

  /**
   * Markdown do relatório → HTML confiável: o HTML cru do LLM é escapado ANTES
   * do marked (só a marcação md sobrevive), e por cima entram os destaques da
   * tela — citações entre aspas e "#id" como botão (clique via delegação em
   * cliqueNoTexto, porque innerHTML não liga eventos do Angular).
   */
  private rendaMarkdown(texto: string): SafeHtml {
    const escapado = texto.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    let html = String(marked.parse(escapado));
    html = html.replace(/“([^”<]{2,200})”/g, '<span class="qmia-citacao">“$1”</span>');
    html = html.replace(/#(\d+)/g,
      '<button type="button" class="qmia-ref" data-atendimento-id="$1" title="Abrir esta conversa na aba Conversas">#$1</button>');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /** Delegação de clique no corpo renderizado: só os "#id" interessam. */
  cliqueNoTexto(evento: Event): void {
    const alvo = (evento.target as HTMLElement)?.closest?.('[data-atendimento-id]');
    if (!alvo) return;
    this.abraAtendimento(Number(alvo.getAttribute('data-atendimento-id')));
  }

  /**
   * Título do item em pedaços renderizáveis, sempre fechando no "#id" da
   * conversa que o caso cita. Se o relatório já pôs o "#id" no título, usa o
   * dele; senão pega o primeiro citado no corpo. Sem título e sem "#id"
   * (o item de balanço do formato antigo), fica só o rótulo.
   */
  private partaTitulo(titulo: string, corpo: string): ParteDoRelatorio[] {
    const partes = this.partaTexto(titulo);
    if (partes.some(parte => parte.tipo === 'ref')) return partes;

    const primeira = corpo.match(/#(\d+)/);
    if (!primeira) return partes.length ? partes : [{ tipo: 'texto', texto: 'Balanço do período' }];

    const ligacao: ParteDoRelatorio = { tipo: 'texto', texto: partes.length ? ' — ' : 'Análise conversa ' };
    return partes.concat([ligacao, { tipo: 'ref', atendimentoId: Number(primeira[1]) }]);
  }

  /** Texto → sequência de pedaços: corrido, "#id" clicável ou citação entre aspas. */
  private partaTexto(texto: string): ParteDoRelatorio[] {
    const partes: ParteDoRelatorio[] = [];
    const padrao = /#(\d+)|[“"]([^”"\n]{2,200})[”"]/g;
    let ultimo = 0;
    let ocorrencia: RegExpExecArray | null;
    while ((ocorrencia = padrao.exec(texto))) {
      if (ocorrencia.index > ultimo) partes.push({ tipo: 'texto', texto: texto.slice(ultimo, ocorrencia.index) });
      if (ocorrencia[1]) partes.push({ tipo: 'ref', atendimentoId: Number(ocorrencia[1]) });
      else partes.push({ tipo: 'citacao', citacao: ocorrencia[2] });
      ultimo = ocorrencia.index + ocorrencia[0].length;
    }
    if (ultimo < texto.length) partes.push({ tipo: 'texto', texto: texto.slice(ultimo) });
    return partes;
  }

  rotuloPrioridade(prioridade: string): string {
    return PRIORIDADES[prioridade] || prioridade;
  }

  gereRelatorio(): void {
    if (this.gerandoRelatorio) return;
    this.gerandoRelatorio = true;
    this.erro = '';
    this.miaV3Service.gereRelatorioAvaliacao({ dias: this.diasSelecionados }).then((resposta: any) => {
      if (resposta?.relatorio) {
        const relatorio = this.estruture(resposta.relatorio);
        this.relatorios = [relatorio, ...this.relatorios];
        this.relatorioAberto = relatorio;
      }
    }).catch((resposta: any) => {
      this.erro = resposta?.error?.erro || 'Não consegui gerar o relatório.';
    }).then(() => this.gerandoRelatorio = false);
  }

  selecioneRelatorio(relatorio: any): void {
    this.relatorioAberto = relatorio;
  }

  // ---------- conversa selecionada ----------

  selecione(item: ItemDaLista): void {
    this.abraAtendimento(item.atendimentoId);
  }

  /** Abre a conversa pelo id — do clique na lista ou num "#id" do relatório. */
  abraAtendimento(atendimentoId: number): void {
    if (!atendimentoId) return;
    this.aba = 'conversas';
    if (this.selecionadoId === atendimentoId && this.detalhe) return;
    this.selecionadoId = atendimentoId;
    this.carregueDetalhe(atendimentoId);
  }

  private carregueDetalhe(atendimentoId: number): void {
    this.carregandoDetalhe = true;
    this.erro = '';
    this.miaV3Service.obtenhaAvaliacao(atendimentoId).then((detalhe: any) => {
      // Resposta de clique antigo chegando atrasada não sobrescreve a atual.
      if (this.selecionadoId !== atendimentoId) return;
      this.aponteDetalhe(detalhe);
    }).catch(() => {
      this.erro = 'Não consegui abrir a conversa.';
    }).then(() => this.carregandoDetalhe = false);
  }

  /** Avaliar (ou reavaliar): dispara o juiz sobre a conversa que está na tela. */
  avalie(): void {
    const atendimentoId = this.selecionadoId;
    if (!atendimentoId || this.avaliando) return;
    this.avaliando = true;
    this.erro = '';
    this.miaV3Service.avalieAtendimento(atendimentoId).then(() => {
      return this.miaV3Service.obtenhaAvaliacao(atendimentoId);
    }).then((detalhe: any) => {
      if (this.selecionadoId === atendimentoId) this.aponteDetalhe(detalhe);
      this.carregue();
    }).catch((resposta: any) => {
      this.erro = resposta?.error?.erro || 'O juiz não conseguiu avaliar este atendimento.';
    }).then(() => this.avaliando = false);
  }

  private aponteDetalhe(detalhe: any): void {
    this.falhasPorTurno = {};
    for (const falha of detalhe?.falhas || []) {
      if (!falha.turnoId) continue;
      (this.falhasPorTurno[falha.turnoId] = this.falhasPorTurno[falha.turnoId] || []).push(falha);
    }
    this.detalhe = detalhe;
  }

  /** Mobile: volta para a lista (desktop nem mostra o botão). */
  fecheConversa(): void {
    this.selecionadoId = null;
    this.detalhe = null;
    this.falhasPorTurno = {};
  }

  falhasDoTurno(turno: any): any[] {
    return this.falhasPorTurno[turno.id] || [];
  }

  roleAteFalha(falha: any): void {
    if (!falha.turnoId) return;
    const alvo = document.getElementById('qmia-turno-' + falha.turnoId);
    if (!alvo) return;
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Reflow no meio para a animação reiniciar quando clicarem duas vezes na mesma falha.
    alvo.classList.remove('qmia-bolha-wrapper--flash');
    void (alvo as HTMLElement).offsetWidth;
    alvo.classList.add('qmia-bolha-wrapper--flash');
  }

  rastreieTurno(_indice: number, turno: any): number {
    return turno.id;
  }
}
